import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, NgZone, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  Data,
  RoutesRecognized,
  RouteConfigLoadStart,
  RouteConfigLoadEnd
} from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { defer, EMPTY, forkJoin, from, fromEvent, iif, of, Subject, Subscription } from 'rxjs';
import { Configuration } from './app.constants';
import { EmployeeSettingsService } from './modules/shared/services/employee-settings.service';
import { SignalRService } from './modules/shared/services/signalr.service';
import { Employee } from './modules/shared/models/employee';
import { AuthenticationService } from './modules/shared/services/authentication.service';
import { CachingService } from './modules/shared/services/caching.service';
import { GanttEventsEmitter } from './modules/shared/events/gantt.events';
import { HeaderEventsEmitter } from './modules/shared/events/header.events';
import { WindowEventsEmitter } from './modules/shared/events/window.events';
import { ConfigurationService } from './modules/shared/services/configuration.service';
import { AuthenticationManager } from './modules/shared/managers/authenticationManager';
import { LoginEventsEmitter } from './modules/shared/events/login.events';
import { DropdownEventsEmitter } from './modules/shared/events/dropdown.events';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { AuthService } from './modules/auth/auth.service';
import { debounceTime, filter, map, switchMap, tap, takeUntil, catchError } from 'rxjs/operators';
import { Account } from './modules/shared/models/account';
import { capitalize } from './modules/shared/utilities/string.utilities';
import { OnboardingModalComponent } from './modules/settings/components/common/modals/onboarding/onboarding-modal.component';
import { ModuleService } from './modules/shared/services/module.service';
import { environment } from 'src/environments/environment';
import { NotificationsService } from './modules/shared/services/notifications.service';
import { HeaderBarComponent } from './modules/shared/components/common/app/header-bar/header-bar.component';
import { AllowedFiltersService } from './modules/shared/services/allowed-filters.service';
import { take } from 'rxjs/operators';
import moment from 'moment';
import { TimeZoneService } from './modules/shared/services/timeZone.service';
import { LocalisationService } from './modules/shared/services/localisation.service';
import { Capacitor } from '@capacitor/core';
import { PushNotificationService } from './modules/shared/services/push-notifications.service';
import { UtilitiesService } from './modules/shared/services/utilities.service';
import { AlertController, IonMenu } from '@ionic/angular';
import { MobileVersionUpdateService } from './modules/shared/services/mobile-version-update.service';
import { Network } from '@capacitor/network';
import { ComponentEventEmitter } from './modules/shared/events/component.events';
import { HeaderFiltersCachingService } from './modules/shared/services/header-filters-caching.service';
import { GenerateAccountFiltersService } from './modules/shared/services/generate-account-filters.service';
import { FixedHeaderFiltersService } from './modules/shared/services/fixed-header-filters.service';
import { WtTranslationService } from './modules/wt-translation/wt-translation.service';
import { WtStorageService } from './modules/shared/services/wt-storage.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavigationService } from './modules/shared/services/navigation.service';
import { PendoService } from './modules/shared/services/pendo.service';
import { UserAgentService } from './modules/shared/services/user-agent.service';
import { PlanningSettingsService } from './modules/settings/services/planning-settings.service';
import { SelectAccountComponent } from './modules/shared/components/modals/select-account/select-account.component';
import { AccountSettingsService } from './modules/shared/services/account-settings.service';
import { BaseConfigurationViewModel } from './modules/shared/viewModels/baseConfigurationViewModel';
import { DatadogService } from './modules/shared/services/datadog.service';
import { SortingFiltersService } from './modules/shared/services/sorting-filters.service';
import { LayoutStateService } from './modules/shared/services/layout-state.service';

@Component({
    selector: 'app-component',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private endPageTitle: string = 'WeTrack';
  private defaultPageTitle: string = 'Home';

  @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;
  @ViewChild('navbar') navBar: HeaderBarComponent;
  @ViewChild('sidebar', { read: ElementRef }) sideBar: ElementRef<HTMLElement>;
  @ViewChild('routeContainer') routeContainer: ElementRef<HTMLElement>;
  @ViewChild('ionMenu', { read: IonMenu }) ionMenu: IonMenu;

  currentAccount: Account;
  currentEmployee: Employee;
  loading: boolean = true;
  isDevMode: boolean;
  bsModalRef: BsModalRef;
  hasBeenInitialized: boolean = false;
  sidebarIsCollapsed: boolean;
  public isNativeDevice: boolean = false;
  public isSidebarToggled = false;
  public hasInternetConnection: boolean = true;
  public maxPageRequestedByComponent: boolean = false;
  public maxHeightRequestedByComponent: boolean = false;
  private mobileConnectionAlert: HTMLIonAlertElement;
  isNotUnauthorizedPage = true;
  private _isPaddingless: boolean;
  private _currentUrl: string;

  private destroy$ = new Subject<void>();

  constructor(
    public readonly meta: Meta,
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    public readonly title: Title,
    public readonly authenticationService: AuthenticationService,
    public readonly allowedFiltersService: AllowedFiltersService,
    public readonly generateAccountFiltersService: GenerateAccountFiltersService,
    public readonly cachingService: CachingService,
    private readonly headerFiltersCachingService: HeaderFiltersCachingService,
    private readonly fixedHeaderFiltersService: FixedHeaderFiltersService,
    public readonly ganttEventsEmitter: GanttEventsEmitter,
    public readonly headerEventsEmitter: HeaderEventsEmitter,
    public readonly windowEventsEmitter: WindowEventsEmitter,
    public readonly baseConfigurationService: ConfigurationService,
    public readonly configurations: Configuration,
    public readonly authenticationManager: AuthenticationManager,
    public readonly loginEventEmitter: LoginEventsEmitter,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly dropdownEventsEmitter: DropdownEventsEmitter,
    public readonly bsModalService: BsModalService,
    private readonly authService: AuthService,
    private readonly employeeSettingService: EmployeeSettingsService,
    private readonly signalRService: SignalRService,
    private readonly modalService: BsModalService,
    private readonly notificationsService: NotificationsService,
    private readonly timeZoneService: TimeZoneService,
    private readonly pushNotifications: PushNotificationService,
    private readonly planningSettingsService: PlanningSettingsService,
    private readonly utilitiesService: UtilitiesService,
    private readonly alertCtrl: AlertController,
    private readonly componentEventEmitter: ComponentEventEmitter,
    private zone: NgZone,
    private readonly navigationService: NavigationService,
    private readonly moduleService: ModuleService,
    private readonly localisationService: LocalisationService,
    private readonly mobileVersionUpdateService: MobileVersionUpdateService,
    private readonly pendoService: PendoService,
    private readonly wtTranslationService: WtTranslationService,
    private readonly accountSettingsService: AccountSettingsService,
    private readonly wtStorage: WtStorageService,
    private readonly userAgentService: UserAgentService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly datadogService: DatadogService,
    private readonly sortingFiltersService: SortingFiltersService,
    private readonly layoutState: LayoutStateService,
  ) {

    localStorage.setItem('id_token_expires_at', '1');

    this.isDevMode = this.configurations.mode === 'Dev';
    this.subscribeDeeplinking();
  }

  async ngOnInit() {
    console.log('AppComponent ngOnInit called');
    await this.authService.init();
console.log('AppComponent this.authService.init()');
    this.isNativeDevice = Capacitor.getPlatform() !== 'web';

    if (this.isNativeDevice) {
      // const networkStatus = await Network.getStatus();
      // this.hasInternetConnection = networkStatus.connected;

      // if (!this.hasInternetConnection) {
      //   void this.showConnectivityAlert();
      //   return;
      // }

      void App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          void App.exitApp();
        } else {
          window.history.back();
        }
      });
    }

    this.listenMobileDeviceInternetStatusChange();

    console.log('app component trigger login success');

    this.authService.loginSuccess$.subscribe((result) => {
      console.log('Login success:', result);
      this.initApplication();
    });

    // Check if user is already authenticated (e.g., after OAuth redirect on mobile)
    // Use setTimeout to ensure tokens are stored in localStorage before checking
    setTimeout(() => {
      console.log('Checking if user is already authenticated...');
      const isAuth = this.authService.isAuthenticated();
      console.log('isAuthenticated result:', isAuth);

      if (isAuth) {
        // Check if user has previously selected an account
        const hasAccountStored = this.cachingService.isAccountUIDStored();
        const storedAccountUID = this.cachingService.getAccountUID();
        const storedBackendURL = this.cachingService.getBackendAPIUrl();
        console.log('=== APP STARTUP - ACCOUNT CHECK ===');
        console.log('Has account stored:', hasAccountStored);
        console.log('Stored accountUID:', storedAccountUID);
        console.log('Stored backend URL:', storedBackendURL);

        if (hasAccountStored) {
          console.log('✅ User already authenticated with account, triggering initApplication');
          this.initApplication();
        } else {
          console.log('⚠️ User authenticated but no account selected yet, waiting for account selection');
          // Don't call initApplication - let the authorize() flow handle account selection
          // Just trigger authorize to show the account selection modal
          this.authorize();
        }
      } else {
        console.log('❌ User not authenticated, waiting for loginSuccess$ event');
      }
    }, 100);

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initApplication() {
    this.initTitleChangeUponNavigation();
    this.moduleService.initModuleChangeUponNavigation();
    this.moduleService.initChangesUponNavigation();

    this.setIosInputFieldFontSize();
    window['GenerateAccountFilters'] = () => {
      this.generateAccountFiltersService.generateFiltersForAccount(this.currentAccount.id).subscribe();
    };

    this.headerEventsEmitter.menuToggled$.subscribe((toggled) => {
      this.sidebarIsCollapsed = !toggled;
    });

    this.bsModalService.onShown.subscribe(() => {
      const modal = document.querySelector('.modal');

      modal.addEventListener('scroll', () => this.dropdownEventsEmitter.broadcastDropdownPositionChanged());
      this.changeDetectorRef.markForCheck();
    });

    let asyncLoadCount = 0;

    this.navigationService.init();
    this.initRouterEvents(asyncLoadCount);

    if (this.router.url.indexOf('unauthorized') > -1 || this.router.url.indexOf('no-access') > -1) {
      this.loading = false;
    }

    if (this.router.url.indexOf('devtools') > -1) {
      this.loading = false;
    }

    if (this.router.url.indexOf('/external/') > -1 || this.router.url.indexOf('/external-access/') > -1) {
      this.loading = false;
      this.isNotUnauthorizedPage = false;

      setTimeout(() => {
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }, 0);
    }

    this.authorize();
  }

  private initRouterEvents(asyncLoadCount: number): void {
    this.router.events
      .pipe(
        filter(event =>
          event instanceof RouteConfigLoadStart ||
          event instanceof RouteConfigLoadEnd ||
          event instanceof NavigationEnd
        )
      )
      .subscribe((event): void => {
        if (event instanceof RouteConfigLoadStart) {
          asyncLoadCount++;
        } else if (event instanceof RouteConfigLoadEnd) {
          asyncLoadCount--;
        } else if (event instanceof NavigationEnd) {
          if (event.url.indexOf('unauthorized') === -1) {
            this.isNotUnauthorizedPage = true;
          }
          if (event.url.indexOf('unauthorized') > -1 || event.url.indexOf('no-access') > -1) {
            this.loading = false;
            this.isNotUnauthorizedPage = false;
          } else if (event.url.indexOf(`/devtools`) > -1) {
            this.loading = false;
          } else if (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1) {
            this.loading = false;
            this.isNotUnauthorizedPage = false;
            this.changeDetectorRef.detectChanges();
          }
        }

        if (this.hasBeenInitialized || (event instanceof NavigationEnd && (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1))) {
          if (this.isNativeDevice) {
            this.loading = false;
          } else {
            if (event instanceof NavigationEnd && (event.url.indexOf('/external/') > -1 || event.url.indexOf('/external-access/') > -1)) {
              this.loading = false;
            } else {
              this.loading = !!asyncLoadCount;
            }
          }
        }
      });
  }


  private subscribeDeeplinking() {
    void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        console.log('Deep link received:', event.url);

        // Check if this is an OAuth2 callback URL (e.g., com.wetrack.feature2://callback)
        // These are handled by the GenericOAuth2 plugin, so we don't need to process them here
        if (event.url.includes('://callback')) {
          console.log('OAuth2 callback detected, handled by GenericOAuth2 plugin');
          return;
        }

        // Handle universal links (https://feature2.wetrack.com/path)
        // Extract the path from the URL for navigation
        const domain = environment.DOMAIN;
        const pathArray = event.url.split(domain);
        const appPath = pathArray.pop();

        if (appPath && pathArray.length > 0) {
          console.log('Navigating to deep link path:', appPath);
          void this.router.navigateByUrl(appPath);
        } else {
          // Fallback: Try to extract path from https:// URLs
          // Example: https://feature2.wetrack.com/account-guid/planning/list
          const httpsMatch = event.url.match(/https?:\/\/[^\/]+(\/.+)/);
          if (httpsMatch && httpsMatch[1]) {
            console.log('Navigating to extracted path:', httpsMatch[1]);
            void this.router.navigateByUrl(httpsMatch[1]);
          } else {
            console.log('Could not extract navigation path from URL:', event.url);
          }
        }
      });
    });
  }

  initIonMenuSubscriptions() {
    this.utilitiesService.sidebarToggled$.subscribe((isToggled) => {
      this.isSidebarToggled = isToggled;
      this.changeDetectorRef.detectChanges();
      if(this.ionMenu) {
        if (isToggled) {
          void this.ionMenu.open();
        } else {
          void this.ionMenu.close();
        }
      }
    });
  }

  get isGanttRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/gantt`);
  }

  get isEventsDashboardRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/planning/dashboard`);
  }

  get isIncidentsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/control`);
  }
  get isIncidentsReportingPreviewRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/control/reports`) && this.router.url.endsWith('/view');
  }

  get isSettingsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/settings`);
  }

  get isRunsheetsRoute(): boolean {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/runsheet`);
  }

  get isReportingRoute(): boolean {
    return this.router.url.indexOf('reports') !== -1;
  }

  get isReportingPreviewRoute(): boolean {
    return this.isReportingRoute && this.router.url.endsWith('view');
  }
  get isPlanningRoute() {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/planning`);
  }

  get isMyTrackRoute() {
    return this.router.url.startsWith(`/${this.currentAccount.accountUID}/mytrack`);
  }

  get isPaddingless(): boolean {
    if (this._currentUrl !== this.router.url) {
      this._currentUrl = this.router.url;
      this._isPaddingless = (
        this.isGanttRoute ||
        this.isIncidentsRoute ||
        this.isSettingsRoute ||
        this.isRunsheetsRoute ||
        this.isEventsDashboardRoute ||
        this.isPlanningRoute ||
        this.isReportingRoute ||
        this.isMyTrackRoute
      );
    }

    return this._isPaddingless;
  }

  private initWindowEventHandler(event: string, options?: EventListenerOptions) {
    const indexableWindowEventsEmitter = this.windowEventsEmitter as {
      [key: string]: (e: Event) => void;
    };

    const targetElement = event !== 'scroll' ? window : this.routeContainer.nativeElement;

    return fromEvent(targetElement, event, options).pipe(
      event === 'resize' || event === 'scroll' ? debounceTime(100) : tap()
    ).subscribe((e: Event) => {
      indexableWindowEventsEmitter[`broadcastWindow${capitalize(event)}EventTriggered`](e);
    });
  }

  private initWindowEventHandlers() {
    const eventsToListenTo = ['click', 'resize'];
    eventsToListenTo.forEach((event) => this.initWindowEventHandler(event));
    fromEvent(window, 'scroll', { capture: true } as EventListenerOptions).subscribe((e: Event) => {
      this.windowEventsEmitter.broadcastWindowScrollEventTriggered(e);
    });
  }

  private initTitleChangeUponNavigation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof RoutesRecognized),
        map((event) => event as RoutesRecognized),
        map((event) => event.state.root.firstChild),
        map((activatedRouteSnapshot) => {
          while (activatedRouteSnapshot.firstChild) {
            activatedRouteSnapshot = activatedRouteSnapshot.firstChild;
          }

          return activatedRouteSnapshot;
        }),
        map((activatedRouteSnapshot) => activatedRouteSnapshot.data)
      )
      .subscribe((data) => {
        this.setProperitesFromRoutingData(data);
      });
  }

  private setProperitesFromRoutingData(event: Data) {
    const title = event['title'] ? `${event['title'] as string} - ${this.endPageTitle}` : `${this.defaultPageTitle} - ${this.endPageTitle}`;

    this.title.setTitle(this.localisationService.localise(title));

    const metaData = event['meta'] as [] || [];
    for (let i = 0; i < metaData.length; i++) {
      this.meta.updateTag(metaData[i]);
    }
  }

 authorize() {
    console.log('=== authorize() called ===');
    this.loading = true;
    const urlParams = new URLSearchParams(window.location.search);

    // Check if redirected from unauthorized page
    const isFromUnauthorized = urlParams.get('unauthorized') === 'true';

    // Clean up URL query parameters if present
    if (isFromUnauthorized) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }

    // PRIORITY 1: Check if we have a stored accountUID (from previous session)
    // This ensures that on page refresh, we use the stored account instead of losing context
    let accountGuid = this.cachingService.getAccountUID();
    console.log('Authorization - stored accountUID:', accountGuid);

    // PRIORITY 2: If no stored accountUID, try to extract from URL
    // This handles initial navigation or when user navigates to a specific account URL
    if (!accountGuid || accountGuid === '0' || accountGuid === '00000000-0000-0000-0000-000000000000') {
      accountGuid = this.authService.getAccountGuid();
      console.log('Authorization - accountGuid from URL:', accountGuid);
    }

    console.log('Authorization - final accountGuid:', accountGuid);
    console.log('Authorization - isFromUnauthorized:', isFromUnauthorized);

   if (accountGuid && accountGuid !== '0' && accountGuid !== '00000000-0000-0000-0000-000000000000' && !isFromUnauthorized) {
     const email = this.authService.getActiveEmployeeEmail();
     console.log('Authorization - email:', email);

     if (!email) {
       console.error('No email found - cannot verify account access');
       this.loading = false;
       this.changeDetectorRef.detectChanges();
       // Show account selection modal as fallback
       this.showAccountSelectionModal(isFromUnauthorized);
       return;
     }

     this.accountSettingsService.hasAccessToAccount(email, accountGuid)
       .subscribe({
         next:(res) => {
           console.log('AppComponent - hasAccessToAccount response:', res);
           if (res) {
             // CRITICAL FIX: Only use res.apiURL if there's no stored backend URL
             // This prevents overwriting the user's manually selected backend URL
             const storedBackendURL = this.cachingService.getBackendAPIUrl();

             if (storedBackendURL) {
               console.log('AppComponent - Using stored backend URL (user manually selected):', storedBackendURL);
               console.log('AppComponent - Ignoring response apiURL:', res.apiURL);
               // Don't call initBackendAPI - the stored URL takes precedence
             } else {
               console.log('AppComponent - No stored backend URL, using response apiURL:', res.apiURL);
               this.configurations.initBackendAPI(res.apiURL);
             }

             this.initialize(res.accountGUID);
           } else {
             console.warn('AppComponent - User does not have access to account:', accountGuid);
             void this.router.navigate([`/${accountGuid}/unauthorized`]);
           }
         },
         error: (error) => {
           console.error('Error checking account access:', error);
           //If hasAccessToAccount returns an error then redisplay the account selector. Assume it is unauthorized.
           //Form here the user can select a different account or logout.
           //Othewise we get a blank page.
           this.showAccountSelectionModal(true);
           this.loading = false;
           this.changeDetectorRef.detectChanges();
         }
        }
      );
    } else {
      // Show account selection modal
      this.showAccountSelectionModal(isFromUnauthorized);
    }
  }

  private showAccountSelectionModal(isFromUnauthorized: boolean) {
    // Set loading to false so the modal is visible
    this.loading = false;
    this.changeDetectorRef.detectChanges();

    console.log('Showing account selection modal');

    let ref = this.modalService.show(SelectAccountComponent, {
      class: 'modal-full-screen',
      ignoreBackdropClick: true,
      initialState: {
        isInitialLogin: !isFromUnauthorized,
        isFromUnauthorized: isFromUnauthorized
      }
    });

    ref.content.accountSelected.subscribe((account) => {
      this.cachingService.addAccountUID(account.accountGUID);
      this.modalService.hide();
      this.configurations.initBackendAPI(account.apiURL);
      this.initialize(account.accountGUID);
    });
  }

  private initialize(accountGUID: string): void {
  console.log('[AppComponent] initialize - accountGUID:', accountGUID);
  this.loading = true;
  this.changeDetectorRef.detectChanges();

  this.authenticationManager.getConfiguration(accountGUID).pipe(
    takeUntil(this.destroy$),
    tap((baseConfig) => {
      // Store employee and account data
      this.cachingService.AddEmployeeData(baseConfig.employeeToken, baseConfig.accountData.id);
      this.currentAccount = baseConfig.accountData;
      this.currentEmployee = this.cachingService.GetEmployee(this.currentAccount.id);

      // Store account identifiers
      localStorage.setItem('selectedAccountId', this.currentAccount.id.toString());
      if (this.currentAccount.accountUID) {
        this.cachingService.addAccountUID(this.currentAccount.accountUID);
      }
    }),
    // Check if account is active
    filter((baseConfig) => {
      if (!baseConfig.accountData.isActive) {
        void this.router.navigate([`/${baseConfig.accountData.accountUID}/unauthorized`]);
        return false;
      }
      return true;
    }),
    tap((baseConfig) => {
      // Initialize services with configuration data
      this.initializeServicesWithConfig(baseConfig);
    }),
    switchMap((baseConfig) => {
      // Initialize third-party services
      this.initializeThirdPartyServices(baseConfig);

      // Load available accounts
      this.loadAvailableAccounts();

      // Broadcast authorization complete
      this.loginEventEmitter.broadcastAuthorizationComplete();

      // Return the filters initialization observable
      return this.allowedFiltersService.init().pipe(
        switchMap(() =>
          iif(
            () => !this.currentEmployee.onboarded && !this.currentAccount.isHubAccount,
            defer(() => this.initOnboardingModal().pipe(
              switchMap(() => this.initHeaderAndFiltersService())
            )),
            defer(() => this.initHeaderAndFiltersService())
          )
        )
      );
    }),
    catchError((error) => {
      console.error('[AppComponent] Error during initialization:', error);
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      // You might want to show an error message to the user
      return EMPTY; // or throwError(() => error) if you want to propagate
    })
  ).subscribe({
    next: () => {
      this.finalizeInitialization();
    },
    error: (error) => {
      console.error('[AppComponent] Subscription error:', error);
      this.loading = false;
      this.changeDetectorRef.detectChanges();
    }
  });

  // Subscribe to component events (separate subscription)
  this.subscribeToComponentEvents();
}

private initializeThirdPartyServices(baseConfig: BaseConfigurationViewModel): void {
  // No need for from(Promise.resolve()) wrapper
  this.datadogService.initDatadog(baseConfig.datadogApplicationId, baseConfig.datadogClientToken);
  void this.mobileVersionUpdateService.checkForUpdate();
  void this.pushNotifications.initPush(this.currentEmployee.id);
  this.pushNotifications.clearAllPushNotifications();
  this.signalRService.startConnection();
  this.pendoService.init(this.currentEmployee, baseConfig.pendoApiKey);
}

private loadAvailableAccounts(): void {
  const cachedAccounts = this.cachingService.employeeAvailableAccounts;

  if (!cachedAccounts || cachedAccounts.length === 0) {
    const email = this.authService.getActiveEmployeeEmail();

    this.accountSettingsService.getApplicableAccounts(email).pipe(
      takeUntil(this.destroy$),
      catchError((error) => {
        console.error('[AppComponent] Error loading available accounts:', error);
        return of([]); // Return empty array on error
      })
    ).subscribe((accounts) => {
      this.cachingService.addEmployeeAvailableAccounts(accounts);
    });
  }
}

  private initializeServicesWithConfig(baseConfig: BaseConfigurationViewModel): void {
    // Employee settings
    if (baseConfig.employeeSettings) {
      this.employeeSettingsService.initializeSettings(baseConfig.employeeSettings);
    }

    // Sorting modal mappings
    if (baseConfig.sortingModalMapping) {
      this.sortingFiltersService.initializeSortingModalMappings(baseConfig.sortingModalMapping);
    }

    // Notifications
    if (baseConfig.unreadNotificationsCount !== undefined) {
      this.notificationsService.setUnreadCount(baseConfig.unreadNotificationsCount);
    }

    // Time zones and modules
    this.timeZoneService.initAdditionalTimeZonesOnAppLoad(baseConfig.accountTimeZones);
    this.moduleService.initModuleItems();
    this.moduleService.initAccountModules();

    // Language and timezone
    this.wtTranslationService.setLanguageAndLocale(
      this.currentEmployee.languageCode ?? this.wtStorage.getItem('langCode') ?? 'en'
    );
    moment.tz.setDefault(this.timeZoneService.sanitazeTimeZone(baseConfig.accountData.accountSpecificTimeZone));

    // Broadcast employee changed
    this.loginEventEmitter.broadcastCurrentEmployeeChanged(this.currentEmployee);
  }

  private finalizeInitialization(): void {
    this.initTitleChangeUponNavigation();
    this.loading = false;
    this.changeDetectorRef.detectChanges();
    this.initWindowEventHandlers();
    this.initIonMenuSubscriptions();

    // Navigate to control if IMS-only account
    if (this.currentAccount.useIMSOnly &&
        this.router.url.indexOf(`${this.currentAccount.accountUID}/control`) < 0) {
      void this.router.navigate([`${this.currentAccount.accountUID}/control/list`]);
      }

      this.layoutState.isFullscreen$.subscribe((isFullscreen) => {
          this.maxPageRequestedByComponent = isFullscreen;
          this.changeDetectorRef.markForCheck();
      });

      //TODO: Remove when Hub Gantt is updated to use LayoutStateService!
      this.componentEventEmitter.fullPageSpaceRequested$.subscribe((state: boolean) => {
          if (state !== this.maxPageRequestedByComponent) {
              this.maxPageRequestedByComponent = state;
              this.changeDetectorRef.markForCheck();
          }
      });
  }

  private subscribeToComponentEvents(): void {
    this.componentEventEmitter.fullPageSpaceRequested$.subscribe((state: boolean) => {
      this.maxPageRequestedByComponent = state;
      this.changeDetectorRef.markForCheck();
    });

    this.componentEventEmitter.hideHeaderRequested$.subscribe((state: boolean) => {
      this.maxHeightRequestedByComponent = state;
      this.changeDetectorRef.markForCheck();
    });
  }


  initHeaderAndFiltersService() {
    return forkJoin([this.headerFiltersCachingService.init(), this.fixedHeaderFiltersService.init()])
  }

  logout(input: boolean) {
    if (input) {
      this.currentEmployee = null;
      this.currentAccount = null;
    }
  }

  private initOnboardingModal() {
      this.loading = false;
      this.changeDetectorRef.markForCheck();
      this.bsModalRef = this.modalService.show(OnboardingModalComponent, {class: 'modal-full-screen'});
      return this.bsModalRef.onHide.pipe(take(1));
  }

  /**
   * The problem: On iOS if the input fields text is less than 16px, when focusing the input that triggers auto zoom.
   *
   * So when using field we should hook the css with "font-size: var(--input-field-font-size)", the variable will be with 14px default font size.
   * On iOS this method will set the variable to be 16px;
   */
  setIosInputFieldFontSize() {
    const iOSArr = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];
    if (
      iOSArr.includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    ) {
      document.documentElement.style.setProperty('--input-field-font-size', '16px');
    }
  }

  expandSidebar() {
    this.utilitiesService.broadcastSidebarToggle(true);
  }
  collapseSidebar() {
    this.utilitiesService.broadcastSidebarToggle(false);
  }

  listenMobileDeviceInternetStatusChange() {
    void Network.addListener('networkStatusChange', (status) => {
      if (status.connected) {
        this.hideConnectivityAlert();
      } else {
        void this.showConnectivityAlert();
      }
    });
  }

  async showConnectivityAlert() {
    this.mobileConnectionAlert = await this.alertCtrl.create({
      header: 'Internet Connectivity',
      message: 'You must have active internet connection to access the application',
      backdropDismiss: false,
      cssClass: 'alert-mobile-status',
    });
    await this.mobileConnectionAlert.present();
    this.changeDetectorRef.markForCheck();
  }

  hideConnectivityAlert() {
    if (this.mobileConnectionAlert) {
      void this.mobileConnectionAlert.dismiss();
    }
  }

  toggleSidebar() {
    this.utilitiesService.broadcastSidebarToggle(!this.isSidebarToggled);
    this.changeDetectorRef.markForCheck();
  }

  get isMobile(): boolean {
    return this.userAgentService.isXSmall;
  }
}
